import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Truck, X, Package, Plus, Trash2, Tag, Sparkles, Printer, QrCode, FileText, CheckCircle2, AlertCircle, Boxes, MapPin, Phone, User } from 'lucide-react';
import PicSelector from '@/components/shared/PicSelector';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useSession } from '@/lib/SessionContext';
import { checkIsDev, checkIsOwner } from '@/lib/AuthSession';
import { toast } from 'sonner';
import LabelDusPrintModal from '@/Components/production/LabelDusPrintModal';
import { calculateMixBoxAllocation } from '@/utils/dusAllocationHelper';

const computeDefaultTglProduksi = (ord, tasks) => {
  // 1. Tanggal SPK
  if (ord?.spk_printed_date || ord?.spk_date) {
    const d = ord.spk_printed_date || ord.spk_date;
    return String(d).split('T')[0];
  }
  // 2. Mulai Diracik / Mixing task
  const mixTask = tasks?.find(t =>
    t.po_id === ord?.id &&
    (t.task_name?.toLowerCase().includes('racik') ||
     t.task_name?.toLowerCase().includes('mix') ||
     t.role?.toLowerCase().includes('racik'))
  );
  if (mixTask?.started_at) {
    return String(mixTask.started_at).split('T')[0];
  }
  // 3. H-2 dari Tanggal Pengiriman / Hari ini
  const targetDate = ord?.shipped_date ? new Date(ord.shipped_date) : new Date();
  targetDate.setDate(targetDate.getDate() - 2);
  return targetDate.toISOString().split('T')[0];
};

const computeExpDate = (prodDateStr) => {
  if (!prodDateStr) return '';
  try {
    const d = new Date(prodDateStr);
    d.setFullYear(d.getFullYear() + 3);
    return d.toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
};

const SHIPPING_VIA_OPTIONS = [
  'Lalamove', // Prioritas Teratas / Sering Digunakan
  'Ambil Sendiri (Self Pick-up)',
  'Anteraja',
  'Baraka Sarana Tama',
  'Dakota Cargo',
  'Deliveree',
  'GoSend',
  'Grab Express',
  'Indah Logistik / Cargo',
  'J&T Cargo',
  'J&T Express',
  'JNE',
  'JT Express',
  'Kalog (Kereta Api Logistik)',
  'Lion Parcel',
  'Ninja Xpress',
  'Paxel',
  'POS Indonesia',
  'RPX',
  'SAP Express',
  'Sentral Cargo',
  'SiCepat',
  'TIKI',
  'Wahana',
  'Lainnya'
];

const DEFAULT_SATUAN_OPTIONS = [
  'pcs', 'box', 'dus', 'liter', 'L', 'ml', 'kg', 'gram', 'lembar', 'set', 'pak', 'roll', 'karton', 'botol', 'jerigen'
];

export default function SelesaiKirimModal({ order, onConfirm, onCancel, onDeleteShipping, isLoading, allTasks = [], isEditing = false }) {
  const { member } = useSession();
  const isDevOrOwner = checkIsDev(member) || checkIsOwner(member);

  const { data: clientAccounts = [] } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });
  const { clientPOs = [] } = useClientPOs();

  // Cari matching PO / Invoice dari ClientPO
  const matchedPO = useMemo(() => {
    if (!order) return null;
    const cleanOrderNo = String(order?.order_number || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
    const cleanOrderId = String(order?.id || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
    const cleanBrand = String(order?.brand_name || order?.product_name || '').trim().toLowerCase();

    return clientPOs.find(p => {
      const cleanPoId = String(p.id || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
      const pCustName = String(p.customerName || '').toLowerCase();
      const pBrandName = String(p.namaBrand || p.brand_name || '').toLowerCase();

      return (
        p.id === order?.order_number ||
        p.id === order?.id ||
        (cleanOrderNo && cleanPoId && cleanOrderNo === cleanPoId) ||
        (cleanOrderId && cleanPoId && cleanOrderId === cleanPoId) ||
        (order?.notes && p.id && order.notes.includes(p.id)) ||
        (cleanBrand && (pBrandName === cleanBrand || pCustName.includes(cleanBrand)))
      );
    });
  }, [clientPOs, order]);

  const matchedClient = clientAccounts.find(c => c.brand_name?.toLowerCase() === order?.brand_name?.toLowerCase());

  // Kumpulkan semua alamat yang tersedia (dari Invoice PO & Database Klien)
  const availableAddresses = useMemo(() => {
    const list = [];

    // 1. Prioritas Utama: Alamat & Telepon dari Invoice PO
    const poAddress = (matchedPO?.customerAddress && matchedPO.customerAddress !== '-')
      ? matchedPO.customerAddress
      : (matchedPO?.alamat_client || matchedPO?.alamat || order?.customerAddress || order?.alamat_client || order?.alamat || order?.alamat_pengiriman?.alamat_lengkap);

    const poPhone = (matchedPO?.phone && matchedPO.phone !== '-')
      ? matchedPO.phone
      : (matchedPO?.no_telepon || matchedPO?.customerPhone || order?.phone || order?.client_phone || order?.customerPhone || order?.alamat_pengiriman?.telepon);

    const brandOnly = (order?.brand_name || matchedPO?.namaBrand || matchedPO?.brand_name || matchedClient?.brand_name || 'Brand').split(' / ')[0].trim();

    let poPenerima = '';
    if (matchedPO?.penerima && matchedPO.penerima !== '-' && matchedPO.penerima.trim()) {
      poPenerima = matchedPO.penerima.trim();
    } else if (matchedPO?.customerName && matchedPO.customerName !== '-') {
      if (matchedPO.customerName.includes(' / ')) {
        const candidate = matchedPO.customerName.split(' / ')[1]?.trim();
        if (
          candidate &&
          !['client', 'pic', 'asa', 'admin', 'muhammad fadli', brandOnly.toLowerCase()].includes(candidate.toLowerCase())
        ) {
          poPenerima = candidate;
        }
      }
    }

    // Jika kosong, tulis NAMA BRAND (jangan pernah pakai nama PIC)
    const finalPenerima = poPenerima || brandOnly;

    if (poAddress && poAddress !== '-') {
      list.push({
        id: 'invoice-addr',
        penerima: finalPenerima,
        telepon: poPhone && poPhone !== '-' ? poPhone : '',
        alamat_lengkap: poAddress,
        kode_pos: matchedPO?.kode_pos || order?.kode_pos || '',
        is_from_invoice: true
      });
    }

    // 2. Alamat dari Database ClientAccount (Jika ada)
    if (matchedClient?.addresses && Array.isArray(matchedClient.addresses)) {
      matchedClient.addresses.forEach((a, aIdx) => {
        if (!list.some(existing => existing.alamat_lengkap?.toLowerCase() === a.alamat_lengkap?.toLowerCase())) {
          list.push({
            id: a.id || `client-addr-${aIdx}`,
            penerima: a.penerima || brandOnly, // Menggunakan nama Brand jika kosong, jangan pernah pakai pic_name
            telepon: a.telepon || matchedClient.phone || '',
            alamat_lengkap: a.alamat_lengkap,
            kode_pos: a.kode_pos || '',
            is_from_invoice: false
          });
        }
      });
    } else if (matchedClient?.location && matchedClient.location !== '-') {
      if (!list.some(existing => existing.alamat_lengkap?.toLowerCase() === matchedClient.location.toLowerCase())) {
        list.push({
          id: 'client-location',
          penerima: brandOnly, // Menggunakan nama Brand jika kosong, jangan pernah pakai pic_name
          telepon: matchedClient.phone !== '-' ? matchedClient.phone : '',
          alamat_lengkap: matchedClient.location,
          kode_pos: matchedClient.kode_pos || '',
          is_from_invoice: false
        });
      }
    }

    return list;
  }, [matchedPO, matchedClient, order]);

  // Compute available variants and max qtys
  const maxVariants = useMemo(() => {
    const products = (order?.products && order.products.length > 0)
      ? order.products
      : [{
          product_name: order?.product_name || order?.brand_name || 'Produk Utama',
          target_qty: order?.target_qty || order?.final_qty || 0,
        }];

    return products.map(p => {
      const rawName = p.product_name || p.variant_name || 'Produk Utama';
      const strippedName = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
      const labelSize = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0;
      const vName = labelSize ? `${strippedName} (${labelSize}ml)` : rawName;

      const targetQty = Number(p.qty) || Number(p.target_qty) || 0;
      
      let totalRejects = 0;
      allTasks.forEach(task => {
        if (task.po_id === order.id && task.member_progress) {
          Object.values(task.member_progress).forEach(prog => {
            if (prog.status === 'completed' && prog.rejected_items) {
              prog.rejected_items.forEach(rj => {
                if (rj.variantName === rawName || (!rj.variantName && products.length === 1)) {
                  rj.items.forEach(item => {
                    if (item.qty > 0) totalRejects += Number(item.qty);
                  });
                }
              });
            }
          });
        }
      });

      let totalShipped = 0;
      if (!isEditing) {
        (order.shipments || []).forEach(shipment => {
          (shipment.items || []).forEach(item => {
            if (item.variant_name === vName) {
              totalShipped += Number(item.qty_akhir) || 0;
            }
          });
        });
      }

      return {
        variant_name: vName,
        target_qty: targetQty,
        reject_qty: totalRejects,
        shipped_qty: totalShipped,
        max_qty: Math.max(0, targetQty - totalRejects - totalShipped)
      };
    }).filter(v => isEditing ? true : (v.max_qty > 0 || v.target_qty > 0));
  }, [order, allTasks, isEditing]);

  const getDefaultKeterangan = (vName) => {
    if (!vName) return order?.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '';
    const match = vName.match(/\((\d+)\s*ml\)/i);
    if (match) return `${match[1]} ml`;

    const prod = (order?.products || []).find(p => {
      const raw = p.product_name || p.variant_name || '';
      return vName.includes(raw) || raw.includes(vName);
    });
    if (prod) {
      const sz = prod.botol_ukuran_aktual || prod.botol_ukuran_label || prod.botol_ukuran_client || prod.ukuran_botol_ml;
      if (sz) return `${sz} ml`;
    }

    return order?.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '';
  };

  const createEmptyDest = (includeAll = false) => {
    const defTgl = computeDefaultTglProduksi(order, allTasks);
    const defaultAddr = availableAddresses[0];
    return {
      id: Date.now().toString() + Math.random().toString(),
      alamat_option: defaultAddr ? defaultAddr.id : 'none',
      custom_alamat: defaultAddr ? {
        penerima: defaultAddr.penerima || '',
        telepon: defaultAddr.telepon || '',
        alamat_lengkap: defaultAddr.alamat_lengkap || '',
        kode_pos: defaultAddr.kode_pos || ''
      } : (order?.alamat_pengiriman || { penerima: '', telepon: '', alamat_lengkap: '', kode_pos: '' }),
      shipping_via: order?.shipping_via || order?.courier || '',
      ongkir_payer: order?.ongkir_payer || 'customer',
      ongkir_amount: order?.ongkir_amount || '',
      resi: order?.resi || order?.tracking_number || '',
      catatan_surat_jalan: order?.catatan_surat_jalan || order?.notes || '',
      label_size: order?.label_size || 'besar',
      tgl_produksi: defTgl,
      exp_date: computeExpDate(defTgl),
      pcs_per_dus: order?.pcs_per_dus || 100,
      catatan_dus: order?.catatan_dus || '-',
      lebihan_variant: '',
      items: includeAll 
        ? maxVariants.map(v => ({ 
            variant_name: v.variant_name, 
            qty_dikirim: isEditing ? (v.target_qty || v.max_qty) : (v.max_qty || v.target_qty), 
            satuan: 'pcs', 
            catatan: getDefaultKeterangan(v.variant_name), 
            is_custom: false,
            pcs_per_dus: 25,
            catatan_label: '',
            sisa_action: 'pisah'
          }))
        : []
    };
  };

  const [destinations, setDestinations] = useState([]);
  const [pic, setPic] = useState(order?.pic || '');
  const [previewLabelDusIndex, setPreviewLabelDusIndex] = useState(null);

  // Initialize destinations
  useEffect(() => {
    if (destinations.length === 0) {
      if (order?.shipments && order.shipments.length > 0) {
        const mapped = order.shipments.map(s => {
          const defTgl = s.tgl_produksi || computeDefaultTglProduksi(order, allTasks);
          const hasSavedAddr = s.alamat_pengiriman?.penerima || s.alamat?.penerima;
          return {
            id: s.id || Date.now().toString() + Math.random().toString(),
            alamat_option: hasSavedAddr ? 'custom' : (availableAddresses.length > 0 ? availableAddresses[0].id : 'none'),
            custom_alamat: s.alamat_pengiriman || s.alamat || (availableAddresses[0] ? {
              penerima: availableAddresses[0].penerima || '',
              telepon: availableAddresses[0].telepon || '',
              alamat_lengkap: availableAddresses[0].alamat_lengkap || '',
              kode_pos: availableAddresses[0].kode_pos || ''
            } : { penerima: '', telepon: '', alamat_lengkap: '', kode_pos: '' }),
            shipping_via: s.shipping_via || order?.shipping_via || order?.courier || '',
            ongkir_payer: s.ongkir_payer || order?.ongkir_payer || 'customer',
            ongkir_amount: s.ongkir_amount || '',
            resi: s.resi || '',
            catatan_surat_jalan: s.catatan_surat_jalan || order?.catatan_surat_jalan || order?.notes || '',
            label_size: s.label_size || 'besar',
            tgl_produksi: defTgl,
            exp_date: s.exp_date || computeExpDate(defTgl),
            pcs_per_dus: s.pcs_per_dus || 25,
            catatan_dus: s.catatan_dus || '-',
            lebihan_variant: s.lebihan_variant || '',
            items: (s.items && s.items.length > 0)
              ? s.items.map(i => ({ 
                  variant_name: i.variant_name || '', 
                  qty_dikirim: i.qty_akhir !== undefined ? i.qty_akhir : (i.qty_dikirim !== undefined ? i.qty_dikirim : 0), 
                  satuan: i.satuan || 'pcs',
                  catatan: (i.catatan && i.catatan.trim() !== '') ? i.catatan : getDefaultKeterangan(i.variant_name),
                  is_custom: !!i.is_custom,
                  pcs_per_dus: i.pcs_per_dus || s.pcs_per_dus || 25,
                  catatan_label: i.catatan_label || '',
                  sisa_action: i.sisa_action || 'pisah'
                }))
              : maxVariants.map(v => ({ 
                  variant_name: v.variant_name, 
                  qty_dikirim: v.target_qty || v.max_qty, 
                  satuan: 'pcs', 
                  catatan: getDefaultKeterangan(v.variant_name), 
                  is_custom: false,
                  pcs_per_dus: 25,
                  catatan_label: '',
                  sisa_action: 'pisah'
                }))
          };
        });
        setDestinations(mapped);
      } else if (maxVariants.length > 0) {
        const initDest = createEmptyDest(true);
        if (availableAddresses.length > 0) {
          initDest.alamat_option = availableAddresses[0].id;
          initDest.custom_alamat = {
            penerima: availableAddresses[0].penerima || '',
            telepon: availableAddresses[0].telepon || '',
            alamat_lengkap: availableAddresses[0].alamat_lengkap || '',
            kode_pos: availableAddresses[0].kode_pos || ''
          };
        }
        setDestinations([initDest]);
      }
    }
  }, [maxVariants, availableAddresses, destinations.length, isEditing, order]);

  // Jika availableAddresses baru selesai ter-load dan alamat masih 'none', otomatis pilih alamat pertama
  useEffect(() => {
    if (availableAddresses.length > 0 && destinations.length > 0) {
      setDestinations(prevDests => {
        let changed = false;
        const updated = prevDests.map((d, idx) => {
          if (idx === 0 && (d.alamat_option === 'none' || !d.alamat_option)) {
            changed = true;
            return {
              ...d,
              alamat_option: availableAddresses[0].id,
              custom_alamat: {
                penerima: availableAddresses[0].penerima || '',
                telepon: availableAddresses[0].telepon || '',
                alamat_lengkap: availableAddresses[0].alamat_lengkap || '',
                kode_pos: availableAddresses[0].kode_pos || ''
              }
            };
          }
          return d;
        });
        return changed ? updated : prevDests;
      });
    }
  }, [availableAddresses]);

  const addDestination = () => {
    const newDest = createEmptyDest(false);
    setDestinations([...destinations, newDest]);
  };

  const removeDestination = (idx) => {
    setDestinations(destinations.filter((_, i) => i !== idx));
  };

  const updateDest = (idx, field, val) => {
    const newDests = [...destinations];
    newDests[idx][field] = val;
    // If tgl_produksi changed, update exp_date too
    if (field === 'tgl_produksi') {
      newDests[idx].exp_date = computeExpDate(val);
    }
    setDestinations(newDests);
  };

  const updateDestItem = (destIdx, itemIdx, field, val) => {
    const newDests = [...destinations];
    newDests[destIdx].items[itemIdx][field] = val;
    setDestinations(newDests);
  };

  const removeItemFromDest = (destIdx, itemIdx) => {
    const newDests = [...destinations];
    newDests[destIdx].items.splice(itemIdx, 1);
    setDestinations(newDests);
  };

  const addItemToDest = (destIdx, variant_name) => {
    const maxV = maxVariants.find(v => v.variant_name === variant_name);
    if (!maxV) return;
    const newDests = [...destinations];
    newDests[destIdx].items.push({ 
      variant_name, 
      qty_dikirim: maxV.max_qty, 
      satuan: 'pcs', 
      catatan: getDefaultKeterangan(variant_name), 
      is_custom: false,
      pcs_per_dus: 25,
      catatan_label: '',
      sisa_action: 'pisah'
    });
    setDestinations(newDests);
  };

  const addCustomItemToDest = (destIdx) => {
    const newDests = [...destinations];
    newDests[destIdx].items.push({
      variant_name: '',
      qty_dikirim: '',
      satuan: 'pcs',
      catatan: '',
      is_custom: true,
      pcs_per_dus: 25,
      catatan_label: '',
      sisa_action: 'pisah'
    });
    setDestinations(newDests);
  };

  const updateDestAddress = (destIdx, field, val) => {
    const newDests = [...destinations];
    newDests[destIdx].custom_alamat[field] = val;
    setDestinations(newDests);
  };

  const handleSubmit = () => {
    if (!isDevOrOwner && !pic) {
      toast.error('Harap pilih PIC Penanggung Jawab Pengiriman terlebih dahulu.');
      return;
    }

    const finalShipments = destinations.map(d => {
      let finalAlamat = null;
      if (d.alamat_option === 'custom') {
        finalAlamat = d.custom_alamat;
      } else if (d.alamat_option !== 'auto' && d.alamat_option !== 'none') {
        finalAlamat = availableAddresses.find(a => a.id === d.alamat_option);
      }
      return {
        id: d.id,
        alamat_pengiriman: finalAlamat,
        shipping_via: d.shipping_via,
        ongkir_payer: d.ongkir_payer,
        ongkir_amount: d.ongkir_payer === 'pabrik' ? (Number(d.ongkir_amount) || 0) : 0,
        resi: d.resi,
        pic: d.pic || pic || order?.pic || '',
        catatan_surat_jalan: d.catatan_surat_jalan || '',
        label_size: d.label_size || 'besar',
        tgl_produksi: d.tgl_produksi || computeDefaultTglProduksi(order, allTasks),
        exp_date: d.exp_date || computeExpDate(d.tgl_produksi),
        pcs_per_dus: Number(d.pcs_per_dus) || 100,
        catatan_dus: d.catatan_dus || '-',
        lebihan_variant: d.lebihan_variant || '',
        items: d.items.map(i => ({
          variant_name: i.variant_name,
          qty_akhir: Number(i.qty_dikirim) || 0,
          satuan: i.satuan || 'pcs',
          catatan: i.catatan || '',
          is_custom: !!i.is_custom,
          pcs_per_dus: Number(i.pcs_per_dus) || Number(d.pcs_per_dus) || 25,
          catatan_label: i.catatan_label || '',
          sisa_action: i.sisa_action || 'pisah'
        }))
      };
    });

    // To preserve backward compatibility, we can also set the main shipping details to the first shipment
    const mainShipment = finalShipments[0] || {};
    
    // Calculate total final_qty across all shipments
    const totalFinalQty = finalShipments.reduce((sum, ship) => sum + ship.items.reduce((s, i) => s + i.qty_akhir, 0), 0);

    onConfirm({
      shipments: finalShipments,
      // Fallback main fields for legacy components
      shipping_via: mainShipment.shipping_via || '',
      ongkir_payer: mainShipment.ongkir_payer || 'customer',
      ongkir_amount: mainShipment.ongkir_amount || 0,
      resi: mainShipment.resi || '',
      pic: pic || order?.pic || '',
      shipping_details: mainShipment.items || [], // Fallback
      final_qty: totalFinalQty,
    });
  };

  const totalTargetQty = (order?.products && order.products.length > 0)
    ? order.products.reduce((sum, p) => sum + (Number(p.qty) || Number(p.target_qty) || 0), 0)
    : (Number(order?.final_qty) || Number(order?.target_qty) || Number(order?.qty) || 0);

  const qty = totalTargetQty;
  const botolComp = (order?.components || []).find(c => c.category === 'botol');
  const ukuran = order?.ukuran_botol_ml
    ? `${order.ukuran_botol_ml} ml`
    : botolComp?.item_name || (order?.products?.length > 1 ? `${order.products.length} Varian Produk` : '-');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 md:p-6 overflow-y-auto">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-5xl lg:max-w-6xl xl:max-w-7xl border border-border my-2 md:my-4 max-h-[92vh] md:max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border bg-white sticky top-0 rounded-t-2xl z-10 shrink-0">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <Truck className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-display font-bold text-base md:text-lg leading-tight">{isEditing ? 'Edit Pengiriman (Multi-Destinasi)' : 'Atur Pengiriman (Multi-Destinasi)'}</h2>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{order?.brand_name}</p>
            </div>
          </div>
          <button onClick={onCancel} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {(() => {
           // Cek apakah PO sudah selesai 100% (semua varian mencapai target)
           const totalTarget = maxVariants.reduce((sum, v) => sum + v.target_qty - v.reject_qty, 0);
           const totalShippedThisTime = maxVariants.reduce((sum, v) => sum + v.max_qty + v.shipped_qty, 0);
           if (totalShippedThisTime < totalTarget) {
             return (
               <div className="bg-amber-50 border-l-4 border-amber-500 p-4 shrink-0 shadow-sm">
                 <div className="flex">
                   <div className="ml-3">
                      <h3 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" /> Peringatan dari Asa!
                      </h3>
                     <div className="mt-2 text-sm text-amber-700 font-medium">
                       <p>Asa nggak bisa ngasih izin buat pindah status PO ke <b>"Pengiriman"</b> ya, soalnya pesanan ini belum genap 100%! Masih ada kekurangan dari target.</p>
                       <p className="mt-1">Surat Jalan tetap bisa dicetak, tapi status PO akan ditahan di <b>"Dalam Proses"</b> sampai kekurangannya terpenuhi.</p>
                     </div>
                   </div>
                 </div>
               </div>
             );
           }
           return null;
        })()}

        <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto custom-scrollbar flex-1">
          <div className="bg-muted/40 rounded-xl p-3 md:p-4 flex items-center gap-3 md:gap-4">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Package className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{order?.product_name || order?.brand_name}</p>
              <p className="text-xs text-muted-foreground">{ukuran} · {qty > 0 ? `${qty.toLocaleString()} pcs` : 'Qty belum ditentukan'}</p>
            </div>
          </div>

          <div className="space-y-4">
            {destinations.map((dest, dIdx) => (
              <div key={dest.id} className="border border-slate-200 rounded-xl bg-slate-50 shadow-sm">
                <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-slate-200 rounded-t-xl">
                  <h3 className="font-bold text-sm text-slate-800">Destinasi {dIdx + 1}</h3>
                  {destinations.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeDestination(dIdx)} className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 px-2">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus
                    </Button>
                  )}
                </div>
                
                <div className="p-4 space-y-5">
                  {/* Alamat Pengiriman */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-xs font-semibold block">Alamat Pengiriman</Label>
                      {availableAddresses.length > 0 && dest.alamat_option !== 'none' && (
                        <span className="text-[10px] text-[#8C6208] font-bold bg-[#FAF4EB] px-2 py-0.5 rounded border border-[#E6D5B8]">
                          Tersambung ke Invoice / Database
                        </span>
                      )}
                    </div>
                    <Select value={dest.alamat_option} onValueChange={v => {
                      updateDest(dIdx, 'alamat_option', v);
                      if (v !== 'custom' && v !== 'none') {
                        const sel = availableAddresses.find(a => a.id === v);
                        if (sel) {
                          updateDest(dIdx, 'custom_alamat', {
                            penerima: sel.penerima || '',
                            telepon: sel.telepon || '',
                            alamat_lengkap: sel.alamat_lengkap || '',
                            kode_pos: sel.kode_pos || ''
                          });
                        }
                      }
                    }}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Pilih alamat (Opsional)..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableAddresses.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            <span className="inline-flex items-center gap-1.5 text-xs text-slate-800">
                              <MapPin className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
                              <span className="font-semibold text-slate-900">
                                {a.is_from_invoice ? 'Alamat Invoice:' : 'Alamat Klien:'}
                              </span>
                              <span className="text-slate-600">
                                {a.penerima || 'Klien'} {a.telepon ? `(${a.telepon})` : ''} — {a.alamat_lengkap.substring(0, 45)}...
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">
                          <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                            <Plus className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <span>Input Manual Alamat Baru</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="none">
                          <span className="text-xs text-slate-400">Kosongkan (Opsional)</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Pratinjau Alamat Terpilih dari Database / Invoice */}
                    {(() => {
                      const sel = availableAddresses.find(a => a.id === dest.alamat_option);
                      if (sel) {
                        return (
                          <div className="mt-2.5 p-3.5 bg-gradient-to-r from-[#FAF7F2] via-[#FDFBF7] to-white border border-[#E6D5B8] rounded-xl shadow-2xs space-y-2">
                            <div className="flex items-center justify-between border-b border-[#F0E8D9] pb-2">
                              <div className="flex items-center gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs font-bold text-[#302621]">
                                  {sel.is_from_invoice ? 'Alamat & Kontak Terhubung dari Invoice' : 'Alamat Database Klien'}
                                </span>
                              </div>
                              <span className="text-[10px] text-[#8C6208] font-bold bg-[#FAF4EB] px-2 py-0.5 rounded border border-[#E6D5B8]">
                                Siap Kirim
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                              <div className="flex items-center gap-2 text-slate-700 bg-white/80 p-2 rounded-lg border border-slate-100">
                                <User className="w-4 h-4 text-[#8C6208] shrink-0" />
                                <div>
                                  <span className="text-[10px] text-slate-400 font-bold block">Penerima:</span>
                                  <strong className="text-[#302621]">{sel.penerima || '-'}</strong>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-slate-700 bg-white/80 p-2 rounded-lg border border-slate-100">
                                <Phone className="w-4 h-4 text-[#8C6208] shrink-0" />
                                <div>
                                  <span className="text-[10px] text-slate-400 font-bold block">Nomor Telepon:</span>
                                  <strong className="text-[#302621]">{sel.telepon || '-'}</strong>
                                </div>
                              </div>
                            </div>
                            <div className="text-xs flex items-start gap-2 text-slate-700 bg-white/80 p-2 rounded-lg border border-slate-100">
                              <MapPin className="w-4 h-4 text-[#8C6208] shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <span className="text-[10px] text-slate-400 font-bold block">Alamat Pengiriman Lengkap:</span>
                                <p className="leading-snug text-slate-800 font-medium">
                                  {sel.alamat_lengkap}
                                  {sel.kode_pos ? ` (${sel.kode_pos})` : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {dest.alamat_option === 'custom' && (
                      <div className="space-y-2.5 mt-2.5 p-3.5 bg-white border border-[#E6D5B8] rounded-xl shadow-2xs">
                        <p className="text-[11px] font-bold text-[#302621] mb-1">Kustom / Masukkan Alamat Pengiriman Manual:</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-slate-500 font-bold block mb-1">Nama Penerima</Label>
                            <Input placeholder="Nama Penerima" value={dest.custom_alamat.penerima} onChange={e => updateDestAddress(dIdx, 'penerima', e.target.value)} className="h-8 text-xs" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500 font-bold block mb-1">No Telepon</Label>
                            <Input placeholder="Contoh: 08123456789" value={dest.custom_alamat.telepon} onChange={e => updateDestAddress(dIdx, 'telepon', e.target.value)} className="h-8 text-xs" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500 font-bold block mb-1">Alamat Lengkap</Label>
                          <Input placeholder="Alamat Lengkap..." value={dest.custom_alamat.alamat_lengkap} onChange={e => updateDestAddress(dIdx, 'alamat_lengkap', e.target.value)} className="h-8 text-xs" />
                        </div>
                        <div className="w-1/2">
                          <Label className="text-[10px] text-slate-500 font-bold block mb-1">Kode Pos (Opsional)</Label>
                          <Input placeholder="Kode Pos" value={dest.custom_alamat.kode_pos} onChange={e => updateDestAddress(dIdx, 'kode_pos', e.target.value)} className="h-8 text-xs" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">Via Ekspedisi</Label>
                      <SearchableCombobox
                        value={dest.shipping_via}
                        onValueChange={v => updateDest(dIdx, 'shipping_via', v)}
                        options={SHIPPING_VIA_OPTIONS}
                        placeholder="Pilih..."
                        className="w-full bg-white text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">Resi (Opsional)</Label>
                      <Input placeholder="No Resi..." value={dest.resi} onChange={e => updateDest(dIdx, 'resi', e.target.value)} className="bg-white" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold mb-1.5 block">Ongkir Ditanggung</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant={dest.ongkir_payer === 'customer' ? 'default' : 'outline'} className={`h-8 text-xs flex-1 ${dest.ongkir_payer === 'customer' ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300' : 'bg-white text-slate-600'}`} onClick={() => updateDest(dIdx, 'ongkir_payer', 'customer')}>Customer</Button>
                      <Button type="button" variant={dest.ongkir_payer === 'pabrik' ? 'default' : 'outline'} className={`h-8 text-xs flex-1 ${dest.ongkir_payer === 'pabrik' ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300' : 'bg-white text-slate-600'}`} onClick={() => updateDest(dIdx, 'ongkir_payer', 'pabrik')}>Pabrik</Button>
                    </div>
                    {dest.ongkir_payer === 'pabrik' && (
                      <Input type="number" placeholder="Nominal Ongkir Rp..." value={dest.ongkir_amount} onChange={e => updateDest(dIdx, 'ongkir_amount', e.target.value)} className="mt-2 bg-white" />
                    )}
                  </div>

                  {/* Varian Qty */}
                  <div className="bg-white border border-[#E6D5B8]/80 rounded-xl p-3.5 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3.5">
                      <div className="flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5 text-[#8C6208]" />
                        <Label className="text-xs font-bold text-[#302621]">Item yang Dikirim ({dest.items?.length || 0})</Label>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dest.items.filter(i => !i.is_custom).length < maxVariants.length && (
                          <Select onValueChange={(val) => addItemToDest(dIdx, val)} value="">
                            <SelectTrigger className="h-7 text-[11px] font-medium w-32 bg-[#FDFBF7] border-[#D4AF37]/50 hover:border-[#8C6208] text-[#302621] transition-all">
                              <SelectValue placeholder="+ Item PO" />
                            </SelectTrigger>
                            <SelectContent>
                              {maxVariants.filter(mv => !dest.items.some(di => !di.is_custom && di.variant_name === mv.variant_name)).map(mv => (
                                <SelectItem key={mv.variant_name} value={mv.variant_name}>{mv.variant_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => addCustomItemToDest(dIdx)} 
                          className="h-7 text-[11px] px-2.5 bg-[#FAF4EB] border-[#D4AF37] text-[#8C6208] hover:bg-[#F2E5D0] hover:text-[#5E3F00] shadow-xs gap-1.5 font-bold transition-all"
                        >
                          <Plus className="w-3.5 h-3.5 text-[#8C6208]" /> Tambah Custom (Box/Sisa/dll)
                        </Button>
                        {dest.items.length > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => {
                             const newDests = [...destinations];
                             newDests[dIdx].items = [];
                             setDestinations(newDests);
                          }} className="h-7 text-[10px] text-red-600 px-2 hover:bg-red-50">
                            Kosongkan
                          </Button>
                        )}
                        {dest.items.length === 0 && (
                          <Button variant="outline" size="sm" onClick={() => {
                             const newDests = [...destinations];
                             newDests[dIdx].items = maxVariants.map(v => ({ variant_name: v.variant_name, qty_dikirim: v.max_qty, satuan: 'pcs', catatan: getDefaultKeterangan(v.variant_name), is_custom: false }));
                             setDestinations(newDests);
                          }} className="h-7 text-[10px] px-2 bg-white">
                            Pilih Semua
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {dest.items.length === 0 ? (
                        <div className="text-center py-5 text-slate-400 text-xs border border-dashed border-[#E6D5B8] rounded-xl bg-[#FAF8F5]">
                          Belum ada item yang dipilih untuk destinasi ini.
                        </div>
                      ) : dest.items.map((item, iIdx) => {
                        const currentQty = Number(item.qty_dikirim) || 0;
                        const currentPcsPerDus = Math.max(1, Number(item.pcs_per_dus) || Number(dest.pcs_per_dus) || 25);
                        const totalKoli = Math.ceil(currentQty / currentPcsPerDus);

                        if (item.is_custom) {
                          return (
                            <div key={iIdx} className="p-3.5 rounded-xl border border-amber-200/80 bg-amber-50/30 hover:bg-amber-50/50 transition-colors space-y-2.5 shadow-2xs">
                              <div className="flex items-center justify-between gap-2 border-b border-amber-200/60 pb-2">
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  <span className="text-[9px] font-bold text-amber-800 uppercase tracking-wider bg-amber-100 px-1.5 py-0.5 rounded shrink-0">Custom</span>
                                  <Input 
                                    placeholder="Nama Barang / Item Tambahan..." 
                                    value={item.variant_name} 
                                    onChange={e => updateDestItem(dIdx, iIdx, 'variant_name', e.target.value)} 
                                    className="h-7 text-xs font-bold bg-white max-w-sm" 
                                  />
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removeItemFromDest(dIdx, iIdx)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                                <div>
                                  <Label className="text-[10px] font-bold text-slate-600 mb-1 block">Qty</Label>
                                  <Input 
                                    type="number" 
                                    value={item.qty_dikirim} 
                                    onChange={e => updateDestItem(dIdx, iIdx, 'qty_dikirim', e.target.value)} 
                                    className="h-8 text-xs font-bold text-center bg-white" 
                                  />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <Label className="text-[10px] font-bold text-slate-600">Isi per Dus</Label>
                                    <span className="text-[9px] text-[#8C6208] font-extrabold bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                                      {totalKoli} Dus
                                    </span>
                                  </div>
                                  <Input 
                                    type="number" 
                                    min="1"
                                    placeholder="Pcs"
                                    value={item.pcs_per_dus !== undefined && item.pcs_per_dus !== null ? item.pcs_per_dus : (dest.pcs_per_dus || 25)} 
                                    onChange={e => {
                                      const val = e.target.value;
                                      if (val === '') {
                                        updateDestItem(dIdx, iIdx, 'pcs_per_dus', '');
                                      } else {
                                        const num = parseInt(val, 10);
                                        updateDestItem(dIdx, iIdx, 'pcs_per_dus', isNaN(num) ? '' : Math.max(1, num));
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!item.pcs_per_dus || Number(item.pcs_per_dus) < 1) {
                                        updateDestItem(dIdx, iIdx, 'pcs_per_dus', dest.pcs_per_dus || 25);
                                      }
                                    }}
                                    className="h-8 text-xs font-bold bg-white" 
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] font-bold text-slate-600 mb-1 block">Catatan Label Dus</Label>
                                  <Input 
                                    placeholder="Catatan label..." 
                                    value={item.catatan_label || ''} 
                                    onChange={e => updateDestItem(dIdx, iIdx, 'catatan_label', e.target.value)} 
                                    className="h-8 text-xs bg-white" 
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] font-bold text-slate-600 mb-1 block">Keterangan</Label>
                                  <Input 
                                    placeholder="Keterangan..." 
                                    value={item.catatan || ''} 
                                    onChange={e => updateDestItem(dIdx, iIdx, 'catatan', e.target.value)} 
                                    className="h-8 text-xs bg-white" 
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        const maxV = maxVariants.find(mv => mv.variant_name === item.variant_name);
                        return (
                          <div key={iIdx} className="p-3.5 rounded-xl border border-slate-200 bg-white hover:border-[#8C6208]/40 transition-all shadow-2xs space-y-2.5">
                            {/* Header: Nama Variant & Status Maks */}
                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-2.5 h-2.5 rounded-full bg-[#8C6208] shrink-0" />
                                <p className="text-xs font-bold text-[#302621] truncate" title={item.variant_name}>
                                  {item.variant_name}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-[10px] text-[#8C6208] font-bold bg-[#FAF4EB] px-2 py-0.5 rounded border border-[#E6D5B8]">
                                  Maks: {maxV?.max_qty} pcs
                                </span>
                                <Button variant="ghost" size="sm" onClick={() => removeItemFromDest(dIdx, iIdx)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>

                            {/* Form Grid 2 Kolom: Qty Dikirim & Catatan Surat Jalan */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {/* 1. Qty Dikirim */}
                              <div className="sm:col-span-1">
                                <Label className="text-[10px] font-bold text-slate-600 mb-1 block">Qty Dikirim (Pcs)</Label>
                                <Input 
                                  type="number" 
                                  value={item.qty_dikirim} 
                                  onChange={e => updateDestItem(dIdx, iIdx, 'qty_dikirim', e.target.value)} 
                                  className="h-8 text-xs font-bold text-[#302621] bg-slate-50/50" 
                                />
                              </div>

                              {/* 2. Keterangan */}
                              <div className="sm:col-span-2">
                                <Label className="text-[10px] font-bold text-slate-600 mb-1 block">Keterangan</Label>
                                <Input 
                                  placeholder="Contoh: 57 ml, Tester, Batch 1" 
                                  value={item.catatan || getDefaultKeterangan(item.variant_name)} 
                                  onChange={e => updateDestItem(dIdx, iIdx, 'catatan', e.target.value)} 
                                  className="h-8 text-xs bg-slate-50/50" 
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Catatan Surat Jalan (Umum / Serah Terima) */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 shadow-2xs space-y-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#8C6208]" />
                      <span className="text-xs font-bold text-[#302621] uppercase tracking-wider">
                        Catatan Surat Jalan (Umum)
                      </span>
                    </div>
                    <Input
                      placeholder="Catatan umum serah terima kurir/klien (Misal: Titip pos sekuriti, kondisi segel aman)..."
                      value={dest.catatan_surat_jalan}
                      onChange={e => updateDest(dIdx, 'catatan_surat_jalan', e.target.value)}
                      className="h-8 text-xs bg-white border-slate-300"
                    />
                  </div>

                  {/* ======================================================== */}
                  {/* KATEGORI 2: PENGATURAN LABEL DUS (KARDUS PENGIRIMAN)     */}
                  {/* ======================================================== */}
                  <div className="bg-[#FAF7F2] border border-[#E6D5B8] rounded-2xl p-4 md:p-5 shadow-xs space-y-4 mt-4">
                    {/* Header Kategori 2 */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#E6D5B8]/80 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-[#8C6208]/10 border border-[#8C6208]/20 flex items-center justify-center text-[#8C6208] shrink-0">
                          <Package className="w-4.5 h-4.5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-xs font-black text-[#302621] uppercase tracking-wider">
                              Kategori 2: Pengaturan Label Dus (Stiker Kardus)
                            </h4>
                            <span className="text-[9px] bg-[#8C6208]/15 text-[#8C6208] border border-[#8C6208]/30 px-2 py-0.5 rounded-full font-bold">
                              Otomatis Sinkron
                            </span>
                          </div>
                          <p className="text-[10px] text-[#7A695B] mt-0.5">
                            Kuantitas otomatis mengikuti item Surat Jalan di atas. Atur kapasitas dus, penanganan sisa, dan cetak lembar label.
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewLabelDusIndex(dIdx)}
                        className="h-8 text-xs bg-white text-[#8C6208] border-[#D4AF37] hover:bg-[#F9F5EC] gap-1.5 font-bold shadow-xs cursor-pointer shrink-0 self-start sm:self-auto"
                      >
                        <Printer className="w-3.5 h-3.5 text-[#8C6208]" />
                        Preview Label Dus
                      </Button>
                    </div>

                    {/* Rincian Dus per Varian Mengikuti Qty Surat Jalan */}
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-[11px] font-bold text-slate-700 block">
                          Rincian Kardus per Varian (Mengikuti Qty Surat Jalan):
                        </Label>
                        <span className="text-[10px] text-slate-400">
                          Klik preset angka untuk ubah isi dus cepat
                        </span>
                      </div>

                      <div className="space-y-3">
                        {dest.items.map((item, iIdx) => {
                          const qty = Number(item.qty_dikirim) || 0;
                          const cap = Math.max(1, Number(item.pcs_per_dus) || Number(dest.pcs_per_dus) || 25);
                          const numBoxes = Math.ceil(qty / cap);
                          const remainder = qty % cap;
                          const hasRemainder = remainder > 0 && numBoxes > 1;
                          const sisaAction = item.sisa_action || 'pisah';

                          const presets = [20, 25, 50, 65, 100];
                          const quickTags = ['Fragile', 'Hati-hati Cairan', 'Dus Atas', 'Posisi Tegak'];

                          return (
                            <div
                              key={iIdx}
                              className="bg-white border border-[#E6D5B8]/80 rounded-xl p-3.5 sm:p-4 shadow-2xs space-y-3 transition-all hover:border-[#D4AF37]/80 hover:shadow-xs"
                            >
                              {/* Variant Title & Dynamic Box Breakdown Badge */}
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#F0E8D9] pb-2.5">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#8C6208] shrink-0" />
                                    <h5 className="text-xs font-bold text-[#302621] truncate" title={item.variant_name}>
                                      {item.variant_name || `Item ${iIdx + 1}`}
                                    </h5>
                                  </div>
                                  <p className="text-[11px] text-slate-500 mt-0.5 ml-4.5">
                                    Total Dikirim: <strong className="text-[#302621] font-bold">{qty.toLocaleString('id-ID')} pcs</strong>
                                  </p>
                                </div>

                                {/* Dynamic Box Status Chips */}
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {qty === 0 ? (
                                    <span className="text-[10px] text-slate-400 italic">Belum ada kuantitas</span>
                                  ) : remainder === 0 ? (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                      {numBoxes} Dus Penuh (@{cap} pcs) · Pas Tanpa Sisa
                                    </span>
                                  ) : (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                        <Package className="w-3.5 h-3.5 text-slate-500" />
                                        {numBoxes - 1} Dus Penuh (@{cap} pcs)
                                      </span>
                                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                                        sisaAction === 'gabung' 
                                          ? 'bg-blue-50 text-blue-800 border-blue-200' 
                                          : 'bg-amber-50 text-amber-800 border-amber-300'
                                      }`}>
                                        <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                                        1 Dus Sisa ({remainder} pcs) {sisaAction === 'gabung' ? '· Gabung Dus Lain' : '· Dus Sendiri'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Row: Quick Capacity Presets & Custom Input */}
                              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                                {/* Presets & Custom Pcs/Dus */}
                                <div className="md:col-span-6 space-y-1">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                                      Kapasitas Dus (Isi / Dus):
                                    </Label>
                                    {remainder === 0 && qty > 0 && (
                                      <span className="text-[9px] font-bold text-emerald-600 flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" /> Pas tanpa sisa
                                      </span>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {presets.map(pVal => {
                                      const isSelected = cap === pVal;
                                      const isEven = qty > 0 && qty % pVal === 0;
                                      return (
                                        <button
                                          key={pVal}
                                          type="button"
                                          onClick={() => updateDestItem(dIdx, iIdx, 'pcs_per_dus', pVal)}
                                          className={`px-2.5 py-1 text-xs rounded-lg font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                            isSelected
                                              ? 'bg-[#302621] text-[#D4AF37] shadow-xs border border-[#302621]'
                                              : isEven
                                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100'
                                              : 'bg-[#FAF7F2] text-slate-700 border border-[#E6D5B8] hover:bg-[#F3ECE0]'
                                          }`}
                                          title={isEven ? `${pVal} pcs membagi habis ${qty} pcs tanpa sisa!` : `${pVal} pcs per dus`}
                                        >
                                          {pVal} pcs
                                        </button>
                                      );
                                    })}

                                    {/* Custom Input */}
                                    <div className="relative w-20 ml-0.5">
                                      <Input
                                        type="number"
                                        min="1"
                                        placeholder="Pcs"
                                        value={item.pcs_per_dus !== undefined && item.pcs_per_dus !== null ? item.pcs_per_dus : cap}
                                        onChange={e => {
                                          const val = e.target.value;
                                          if (val === '') {
                                            updateDestItem(dIdx, iIdx, 'pcs_per_dus', '');
                                          } else {
                                            const num = parseInt(val, 10);
                                            updateDestItem(dIdx, iIdx, 'pcs_per_dus', isNaN(num) ? '' : Math.max(1, num));
                                          }
                                        }}
                                        onBlur={() => {
                                          if (!item.pcs_per_dus || Number(item.pcs_per_dus) < 1) {
                                            updateDestItem(dIdx, iIdx, 'pcs_per_dus', cap || 25);
                                          }
                                        }}
                                        className="h-7 text-xs font-bold text-center pr-6 bg-slate-50 border-slate-300"
                                      />
                                      <span className="absolute right-1.5 top-1.5 text-[9px] text-slate-400 font-bold">pcs</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Catatan Label Stiker */}
                                <div className="md:col-span-6 space-y-1">
                                  <Label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">
                                    Catatan Stiker Label Dus:
                                  </Label>
                                  <Input
                                    placeholder="Misal: Fragile / Top Box / Simpan Suhu Ruang..."
                                    value={item.catatan_label || ''}
                                    onChange={e => updateDestItem(dIdx, iIdx, 'catatan_label', e.target.value)}
                                    className="h-7 text-xs bg-slate-50 border-slate-300"
                                  />
                                  <div className="flex items-center gap-1 flex-wrap pt-0.5">
                                    <span className="text-[9px] text-slate-400 font-bold mr-0.5">Cepat:</span>
                                    {quickTags.map(tag => (
                                      <button
                                        key={tag}
                                        type="button"
                                        onClick={() => {
                                          const curr = (item.catatan_label || '').trim();
                                          const updated = curr ? (curr.includes(tag) ? curr : `${curr} · ${tag}`) : tag;
                                          updateDestItem(dIdx, iIdx, 'catatan_label', updated);
                                        }}
                                        className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-[#F9F5EC] hover:text-[#8C6208] text-slate-600 border border-slate-200 transition-colors font-medium cursor-pointer"
                                      >
                                        +{tag}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Sisa Handling Box (Hanya muncul jika ada sisa > 0) */}
                              {hasRemainder && (
                                <div className="bg-gradient-to-r from-[#FDF8EE] to-[#FAF5EB] border border-[#E8D4A8] rounded-xl p-3 space-y-2 mt-2 shadow-2xs">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                      <span className="text-[11px] font-bold text-[#8C6208]">
                                        Pengaturan Kardus Terakhir / Sisa ({remainder} pcs):
                                      </span>
                                    </div>
                                    <span className="text-[10px] text-slate-500">
                                      Dus ke-{numBoxes} berisi {remainder} dari kapasitas {cap} pcs
                                    </span>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {/* Opsi 1: Dus Sendiri */}
                                    <button
                                      type="button"
                                      onClick={() => updateDestItem(dIdx, iIdx, 'sisa_action', 'pisah')}
                                      className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer flex items-start gap-2.5 ${
                                        sisaAction !== 'gabung'
                                          ? 'bg-white border-[#8C6208] ring-1 ring-[#8C6208]/30 shadow-xs'
                                          : 'bg-white/60 border-slate-200 hover:bg-white text-slate-600'
                                      }`}
                                    >
                                      <div className={`w-4 h-4 rounded-full mt-0.5 flex items-center justify-center shrink-0 border ${
                                        sisaAction !== 'gabung' ? 'border-[#8C6208] bg-[#8C6208] text-white' : 'border-slate-300 bg-white'
                                      }`}>
                                        {sisaAction !== 'gabung' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs font-bold text-[#302621] flex items-center gap-1.5">
                                          <Package className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
                                          Kardus Tersendiri ({remainder} pcs)
                                        </p>
                                        <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                                          Dus ke-{numBoxes} dicetak stiker terpisah khusus {remainder} pcs.
                                        </p>
                                      </div>
                                    </button>

                                    {/* Opsi 2: Gabung Dus Lain */}
                                    <button
                                      type="button"
                                      onClick={() => updateDestItem(dIdx, iIdx, 'sisa_action', 'gabung')}
                                      className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer flex items-start gap-2.5 ${
                                        sisaAction === 'gabung'
                                          ? 'bg-white border-[#8C6208] ring-1 ring-[#8C6208]/30 shadow-xs'
                                          : 'bg-white/60 border-slate-200 hover:bg-white text-slate-600'
                                      }`}
                                    >
                                      <div className={`w-4 h-4 rounded-full mt-0.5 flex items-center justify-center shrink-0 border ${
                                        sisaAction === 'gabung' ? 'border-[#8C6208] bg-[#8C6208] text-white' : 'border-slate-300 bg-white'
                                      }`}>
                                        {sisaAction === 'gabung' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs font-bold text-[#302621] flex items-center gap-1.5">
                                          <Boxes className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
                                          Gabung Dus Campur
                                        </p>
                                        <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                                          Hemat dus. Sisa {remainder} pcs digabung varian lain (Stiker: Dus Campuran).
                                        </p>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Widget Alokasi Dus Campur & Dus Lebihan (Muncul jika ada >= 2 varian dengan sisa yang digabung) */}
                    {(() => {
                      const gabungItemsWithRem = dest.items.filter(item => {
                        const q = Number(item.qty_dikirim) || 0;
                        const c = Math.max(1, Number(item.pcs_per_dus) || Number(dest.pcs_per_dus) || 25);
                        return (item.sisa_action === 'gabung') && (q % c > 0);
                      }).map(item => {
                        const q = Number(item.qty_dikirim) || 0;
                        const c = Math.max(1, Number(item.pcs_per_dus) || Number(dest.pcs_per_dus) || 25);
                        return {
                          ...item,
                          remainder: q % c
                        };
                      });

                      if (gabungItemsWithRem.length <= 1) return null;

                      const mixBoxCap = Math.max(1, Number(gabungItemsWithRem[0]?.pcs_per_dus) || Number(dest.pcs_per_dus) || 65);
                      const alloc = calculateMixBoxAllocation(gabungItemsWithRem, mixBoxCap, dest.lebihan_variant);
                      const { isOverflow, totalPooledRem, box1, box2, targetLebihanVar } = alloc;

                      return (
                        <div className="bg-gradient-to-br from-[#FFFDF9] via-[#FAF6ED] to-[#F5EFE1] border-2 border-[#D4AF37]/60 rounded-xl p-4 shadow-sm space-y-3 mt-4">
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E6D5B8] pb-2.5">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-[#8C6208] text-white flex items-center justify-center font-bold shadow-2xs">
                                <Boxes className="w-4 h-4" />
                              </div>
                              <div>
                                <h4 className="text-xs font-black text-[#302621] uppercase tracking-wide flex items-center gap-2">
                                  Alokasi Dus Campuran & Dus Lebihan
                                  <span className="text-[10px] bg-[#FAF4EB] text-[#8C6208] border border-[#E6D5B8] font-bold px-2.5 py-0.5 rounded-full">
                                    Hemat 1 Dus
                                  </span>
                                </h4>
                                <p className="text-[11px] text-slate-600 mt-0.5">
                                  Total sisa gabungan: <strong className="text-[#8C6208]">{totalPooledRem} pcs</strong> ({gabungItemsWithRem.map(i => `${i.variant_name?.split(' (')[0] || i.variant_name}: ${i.remainder} pcs`).join(' + ')}) · Kapasitas 1 Dus: <strong>{mixBoxCap} pcs</strong>
                                </p>
                              </div>
                            </div>
                          </div>

                          {isOverflow ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Dus 1: Dus Campuran Penuh */}
                              <div className="bg-white p-3.5 rounded-xl border border-emerald-300 shadow-2xs space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-black text-emerald-800 flex items-center gap-1.5">
                                    <Package className="w-3.5 h-3.5 text-emerald-600" />
                                    1. DUS CAMPURAN (MIX)
                                  </span>
                                  <span className="text-[10px] font-extrabold bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-300">
                                    Penuh Pas ({box1.total} pcs)
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-500">
                                  Kardus campuran ini terisi penuh kapasitas {box1.total} pcs dengan rincian:
                                </p>
                                <div className="p-2 bg-emerald-50/50 rounded-lg border border-emerald-100 text-[11px] text-slate-700 space-y-1.5 font-medium">
                                  {box1.items.map(it => (
                                    <div key={it.name} className="flex justify-between items-center">
                                      <span>• {it.name}:</span>
                                      <strong className="text-emerald-900">{it.qty} pcs</strong>
                                    </div>
                                  ))}
                                  <div className="border-t border-emerald-200 pt-1 flex justify-between items-center font-bold text-emerald-900 text-xs">
                                    <span>Total di Dus Mix:</span>
                                    <span>{box1.total} pcs</span>
                                  </div>
                                </div>
                                <span className="text-[9px] text-slate-400 block italic">
                                  Label stiker: [DUS CAMPURAN / FULL MIX - {box1.total} PCS]
                                </span>
                              </div>

                              {/* Dus 2: Dus Lebihan / Sisa */}
                              <div className="bg-white p-3.5 rounded-xl border border-amber-300 shadow-2xs space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-black text-amber-800 flex items-center gap-1.5">
                                    <Boxes className="w-3.5 h-3.5 text-amber-600" />
                                    2. DUS LEBIHAN / SISA CAMPURAN
                                  </span>
                                  <span className="text-[10px] font-extrabold bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full border border-amber-300">
                                    Sisa: {box2.total} pcs
                                  </span>
                                </div>
                                <div className="space-y-1.5">
                                  <label className="text-[10px] font-bold text-slate-700 block">
                                    Prioritaskan varian di Dus Lebihan:
                                  </label>
                                  <div className="flex flex-wrap gap-1.5">
                                    {gabungItemsWithRem.map(it => {
                                      const isSel = it.variant_name === targetLebihanVar;
                                      return (
                                        <button
                                          key={it.variant_name}
                                          type="button"
                                          onClick={() => updateDest(dIdx, 'lebihan_variant', it.variant_name)}
                                          className={`text-[11px] px-2.5 py-1 rounded-lg font-bold border transition-all cursor-pointer ${
                                            isSel
                                              ? 'bg-[#302621] text-white border-[#302621] shadow-2xs'
                                              : 'bg-slate-50 hover:bg-amber-50 text-slate-700 border-slate-200 hover:border-amber-300'
                                          }`}
                                        >
                                          {it.variant_name?.split(' (')[0] || it.variant_name} (Sisa {it.remainder} pcs)
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="p-2 bg-amber-50/50 rounded-lg border border-amber-100 text-[11px] text-slate-700 space-y-1.5 font-medium">
                                  <p className="text-[10px] text-slate-500 font-normal mb-1">
                                    Kardus kedua menampung sisa gabungan ({box2.total} pcs):
                                  </p>
                                  {box2.items.map(it => (
                                    <div key={it.name} className="flex justify-between items-center">
                                      <span>• {it.name}:</span>
                                      <strong className="text-amber-900">{it.qty} pcs</strong>
                                    </div>
                                  ))}
                                  <div className="border-t border-amber-200 pt-1 flex justify-between items-center font-bold text-amber-900 text-xs">
                                    <span>Total di Dus Lebihan:</span>
                                    <span>{box2.total} pcs</span>
                                  </div>
                                </div>
                                <span className="text-[9px] text-slate-400 block italic">
                                  Label stiker: [DUS LEBIHAN: {box2.items.map(i => `${i.name?.split(' (')[0] || i.name} ${i.qty} pcs`).join(' + ')}]
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-white rounded-xl border border-emerald-200 text-xs text-slate-700 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span>Semua sisa gabungan ({totalPooledRem} pcs) pas masuk ke <strong>1 Dus Campuran</strong> (kapasitas {mixBoxCap} pcs).</span>
                              </div>
                              <span className="font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 text-[10px]">
                                1 Dus Campuran ({totalPooledRem} pcs)
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Ringkasan Kardus & Format Label Footer */}
                    {(() => {
                      let totalPcsShipped = 0;
                      let separateBoxesCount = 0;
                      let separateFullCount = 0;
                      let separateRemCount = 0;
                      let gabungFullCount = 0;
                      let gabungPooledRem = 0;

                      const gabungItemsList = dest.items.filter(i => i.sisa_action === 'gabung');
                      const hasMultipleGabung = gabungItemsList.length > 1;

                      dest.items.forEach(item => {
                        const q = Number(item.qty_dikirim) || 0;
                        const c = Math.max(1, Number(item.pcs_per_dus) || Number(dest.pcs_per_dus) || 25);
                        totalPcsShipped += q;

                        if (hasMultipleGabung && item.sisa_action === 'gabung') {
                          const fullN = Math.floor(q / c);
                          const rem = q % c;
                          gabungFullCount += fullN;
                          gabungPooledRem += rem;
                        } else {
                          const n = Math.ceil(q / c);
                          const rem = q % c;
                          separateBoxesCount += n;
                          if (rem > 0 && n > 1) {
                            separateFullCount += (n - 1);
                            separateRemCount += 1;
                          } else if (n > 0) {
                            separateFullCount += n;
                          }
                        }
                      });

                      const mixBoxCap = Math.max(1, Number(gabungItemsList[0]?.pcs_per_dus) || Number(dest.pcs_per_dus) || 65);
                      let mixedBoxesCount = 0;
                      let mixedFullCount = 0;
                      let mixedRemCount = 0;

                      if (hasMultipleGabung && gabungPooledRem > 0) {
                        mixedBoxesCount = Math.ceil(gabungPooledRem / mixBoxCap);
                        if (gabungPooledRem > mixBoxCap) {
                          mixedFullCount = Math.floor(gabungPooledRem / mixBoxCap);
                          mixedRemCount = (gabungPooledRem % mixBoxCap) > 0 ? 1 : 0;
                        } else {
                          mixedRemCount = 1;
                        }
                      }

                      const totalBoxesCount = separateBoxesCount + gabungFullCount + mixedBoxesCount;
                      const fullBoxesTotal = separateFullCount + gabungFullCount + mixedFullCount;
                      const remBoxesTotal = separateRemCount + mixedRemCount;

                      const isBesar = (dest.label_size || 'besar') === 'besar';
                      const perSheet = isBesar ? 8 : 15;
                      const sheetCount = Math.ceil(totalBoxesCount / perSheet) || 1;

                      return (
                        <div className="bg-white border border-[#E6D5B8] rounded-xl p-4 shadow-2xs space-y-4 pt-4">
                          {/* Top row: Summary stats & CTA */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[#F0E8D9]">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-xl bg-[#FAF4EB] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208] shrink-0">
                                <Boxes className="w-4 h-4 text-[#8C6208]" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-[#302621] uppercase tracking-wider">
                                    Ringkasan Kardus & Lembar Label
                                  </span>
                                  <span className="text-[10px] bg-[#8C6208] text-white px-2 py-0.5 rounded-full font-bold">
                                    {totalBoxesCount} Dus
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-600 mt-0.5">
                                  Total <strong className="text-[#302621]">{totalPcsShipped.toLocaleString('id-ID')} pcs</strong> dikemas dalam{' '}
                                  <strong className="text-[#8C6208]">{totalBoxesCount} Dus</strong>
                                  {remBoxesTotal > 0 ? ` (${fullBoxesTotal} Penuh + ${remBoxesTotal} Dus Sisa/Lebihan)` : ' (Semua Penuh Pas)'}
                                  {hasMultipleGabung && (
                                    <span className="text-[#8C6208] font-bold ml-1">· Hemat 1 Dus</span>
                                  )}
                                  {' '}· Butuh <strong className="text-[#302621]">{sheetCount} Lembar Kertas A4</strong>
                                </p>
                              </div>
                            </div>

                            <Button
                              type="button"
                              onClick={() => setPreviewLabelDusIndex(dIdx)}
                              className="bg-[#302621] hover:bg-[#43352E] text-[#D4AF37] border border-[#D4AF37]/40 shadow-xs font-bold gap-2 text-xs h-9 shrink-0 cursor-pointer"
                            >
                              <Printer className="w-4 h-4 text-[#D4AF37]" />
                              Preview & Cetak Stiker Dus ({totalBoxesCount} Dus)
                            </Button>
                          </div>

                          {/* Bottom controls: Ukuran, Tgl Produksi, Exp Date */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-[10px] font-bold text-slate-600 mb-1 block uppercase tracking-wider">
                                Format Ukuran Stiker Label
                              </Label>
                              <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg border border-slate-200">
                                <button
                                  type="button"
                                  onClick={() => updateDest(dIdx, 'label_size', 'besar')}
                                  className={`py-1 text-[11px] font-bold rounded transition-all cursor-pointer ${
                                    (dest.label_size || 'besar') === 'besar' ? 'bg-[#302621] text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                                  }`}
                                >
                                  Besar (7×7.5cm · 8/A4)
                                </button>
                                <button
                                  type="button"
                                  onClick={() => updateDest(dIdx, 'label_size', 'kecil')}
                                  className={`py-1 text-[11px] font-bold rounded transition-all cursor-pointer ${
                                    dest.label_size === 'kecil' ? 'bg-[#302621] text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                                  }`}
                                >
                                  Kecil (5.4×5.8cm · 15/A4)
                                </button>
                              </div>
                            </div>

                            <div>
                              <Label className="text-[10px] font-bold text-slate-600 mb-1 block uppercase tracking-wider">
                                Tgl Produksi
                              </Label>
                              <Input
                                type="date"
                                value={dest.tgl_produksi}
                                onChange={e => updateDest(dIdx, 'tgl_produksi', e.target.value)}
                                className="h-8 text-xs bg-slate-50 font-medium"
                              />
                            </div>

                            <div>
                              <Label className="text-[10px] font-bold text-slate-600 mb-1 block uppercase tracking-wider">
                                Exp Date (+3 Thn Auto)
                              </Label>
                              <Input
                                type="date"
                                value={dest.exp_date}
                                onChange={e => updateDest(dIdx, 'exp_date', e.target.value)}
                                className="h-8 text-xs bg-slate-50 font-medium"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addDestination} className="w-full border-dashed border-slate-300 text-slate-600 bg-white hover:bg-slate-50 gap-2 h-10">
              <Plus className="w-4 h-4" /> Tambah Destinasi Lainnya
            </Button>
          </div>

          <div className="border-t border-border pt-6">
            <PicSelector
              label="PIC Penanggung Jawab Pengiriman"
              value={pic}
              onChange={setPic}
              placeholder="Pilih PIC..."
              requiredPageAccessKey="delivery"
            />
          </div>
        </div>

        <div className="flex gap-2 p-4 md:p-6 pt-3 md:pt-4 bg-white sticky bottom-0 rounded-b-2xl border-t border-border shrink-0">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isLoading}>
            Batal
          </Button>
          {isEditing && onDeleteShipping && (
            <Button
              variant="outline"
              className="border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1.5 font-bold"
              onClick={onDeleteShipping}
              disabled={isLoading}
              type="button"
            >
              <Trash2 className="w-4 h-4" /> Hapus Surat Jalan
            </Button>
          )}
          <Button
            className={`flex-1 gap-2 font-bold text-white shadow-sm ${
              (!isDevOrOwner && !pic) ? 'bg-slate-400 cursor-not-allowed hover:bg-slate-400' : 'bg-green-600 hover:bg-green-700'
            }`}
            onClick={handleSubmit}
            disabled={isLoading || (!isDevOrOwner && !pic)}
          >
            <Truck className="w-4 h-4" />
            {isLoading ? 'Memproses...' : 'Simpan Semua Pengiriman'}
          </Button>
        </div>
      </div>

      {/* Test Preview Label Dus Modal */}
      {previewLabelDusIndex !== null && destinations[previewLabelDusIndex] && (
        <LabelDusPrintModal
          isOpen={previewLabelDusIndex !== null}
          onClose={() => setPreviewLabelDusIndex(null)}
          order={order}
          shipment={destinations[previewLabelDusIndex]}
          allTasks={allTasks}
          initialSettings={{
            label_size: destinations[previewLabelDusIndex].label_size,
            tgl_produksi: destinations[previewLabelDusIndex].tgl_produksi,
            exp_date: destinations[previewLabelDusIndex].exp_date,
            pcs_per_dus: destinations[previewLabelDusIndex].pcs_per_dus,
            catatan_dus: destinations[previewLabelDusIndex].catatan_dus,
            variant_name: destinations[previewLabelDusIndex].items?.length > 1 ? 'all' : destinations[previewLabelDusIndex].items?.[0]?.variant_name
          }}
        />
      )}
    </div>
  );
}